// Images of a converted skeleton drawn exactly as Rive's web renderer draws them: rive-spine's companion runtime
// (`runtime/riveTextureSampling.ts`, copied 2026-10-11 with its clipped images' level of detail; slot-coin-hold's copy
// first, the engine's since decision 72), trimmed to what `SpineArtboard` uses: the pages are decoded from the bundle
// the loader fetched (`decodeRiveAtlasPages`, which also takes a page the browser would not decode ahead: ported back
// to rive-spine the same day), the resolution is set before each offscreen render (`setRiveSpineResolution`), and the
// dither alignment for pixel-exact comparisons is gone. Its solid colour fills (2026-10-13: a Rive shape filled with one
// colour, which the converter draws as an image of it) are drawn as Rive draws them.
//
// Rive samples each image from its own mip chain, bilinearly within the nearest level (GL_LINEAR_MIPMAP_NEAREST,
// renderer gl_utils.cpp), at a level of detail it computes once per image draw on the CPU (gpu.cpp):
//
//     lod = 0.5·log2(max(1, max(dudx² + dvdx², dudy² + dvdy²))) - 0.5        (MIP_MAP_LOD_BIAS = -0.5)
//     dudx = m.xx·w, dudy = m.yx·h, dvdx = m.xy·w, dvdy = m.yy·h             (m: device pixels → image unit square)
//
// the usual texels-per-pixel measure, half a level sharp, except that the cross terms take the other side's size:
// for a non-square image drawn rotated, the level comes out up to log2(w/h) coarser than a GPU's own choice. In
// texels, with r = w/h:  ρ² = max((dU/dX)² + (r·dV/dX)², (dU/dY / r)² + (dV/dY)²). Meshes are drawn another way
// (draw_image_mesh): texture(image, uv, -0.5), the GPU's own level of detail, biased.
//
// The converter gives each image its own atlas page (its own texture and mip chain, as in Rive). This gives
// spine-pixi-v8 the rest:
//  - pages decoded as Rive decodes its images (an HTMLImageElement premultiplied by WebGL on upload; Pixi's
//    createImageBitmap premultiplies with other rounding), with the mip chain and nearest-level filter (spine-pixi-v8
//    4.2 turns mipmaps on only after Pixi 8 has sized the texture: the atlas's filter alone leaves one);
//  - Spine slots drawn by a batcher of their own (spine-pixi-v8 otherwise hands them to Pixi's default one): an
//    image's level of detail is computed per draw from its quad, with Rive's formula, and read explicitly; a mesh is
//    sampled exactly as Rive samples it, at the GPU's own level of detail biased -0.5;
//  - each draw's opacity rounded to 8 bits as Rive rounds it (to nearest; Pixi's packing truncates), and every
//    fragment dithered as Rive dithers it (interleaved gradient noise, ±½/256), so stacked faint layers round alike;
//  - a solid colour fill premultiplied as Rive premultiplies it, on the CPU in 8-bit integers, so its dither leaves it.
//
// Rive's canvas is multisampled, so its image and mesh edges are antialiased: the title draws each skeleton into a
// multisampled texture of its own (`SpineArtboard`). WebGL 2 only: elsewhere Spine slots keep Pixi's batcher.
import { BatchableSpineSlot } from "@esotericsoftware/spine-pixi-v8/dist/BatchableSpineSlot.js";
import { darkTintBitGl } from "@esotericsoftware/spine-pixi-v8/dist/darktint/darkTintBit.js";
import { RegionAttachment, type Skeleton } from "@esotericsoftware/spine-pixi-v8";
import {
  Batcher,
  Buffer,
  BufferUsage,
  Color,
  colorBitGl,
  compileHighShaderGl,
  ExtensionType,
  extensions,
  fragmentGlTemplate,
  Geometry,
  getBatchSamplersUniformGroup,
  GlProgram,
  globalUniformsBitGl,
  ImageSource,
  roundPixelsBitGl,
  Shader,
  type TextureSource,
  type BatchableMeshElement,
  type BatchableQuadElement,
  vertexGlTemplate,
} from "pixi.js";

/** Rive's mip level-of-detail bias for images. */
const RIVE_MIP_LOD_BIAS = -0.5;

/** Texture options for a converted skeleton's atlas pages: the mip chain, read at the nearest level. */
const RIVE_ATLAS_TEXTURE_OPTIONS = { autoGenerateMipmaps: true, mipmapFilter: "nearest" } as const;

/** The pages bundle's header (rive-spine's `<name>.pages`): each page's file name, type and place in the body. */
interface PagesHeader {
  pages: { file: string; type: string; offset: number; length: number }[];
}

/** A converted skeleton's decoded pages, by name, and those decoded only as they upload (`decodeRiveAtlasPages`). */
export interface RiveAtlasPages {
  sources: Record<string, TextureSource>;
  /** The pages `decode()` refused, each with its reason: WebGL decodes them as it uploads them. */
  undecoded: { name: string; reason: string }[];
}

/**
 * A converted skeleton's atlas pages (`names`, the atlas's), cut out of its pages bundle (`bytes`: every page in one
 * file, as the .riv carried its images) and decoded the way Rive's web runtime decodes its images: an HTMLImageElement,
 * premultiplied by WebGL as it uploads (decode_image / upload_image), with the mip chain at the nearest level. Pixi
 * decodes through createImageBitmap, which Chrome premultiplies with other rounding: 1/255 more or less on a quarter of
 * the texels, which stacked layers add up. `label` names the bundle in an error.
 *
 * `decode()` asks the page's compositor to decode ahead, and a browser may refuse it for an image that is fine: VS
 * Code's (Chromium 142) refused the pages past some count with "The source image cannot be decoded" (2026-10-11), the
 * later bundles' all. Such a page waits until its image has loaded and is listed in `undecoded`: WebGL decodes it as it
 * uploads, to the same texels byte for byte. Only an image that does not load fails the bundle.
 */
export async function decodeRiveAtlasPages(names: readonly string[], bytes: Uint8Array<ArrayBuffer>, label: string): Promise<RiveAtlasPages> {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (bytes.length < 8 || String.fromCharCode(bytes[0]!, bytes[1]!, bytes[2]!, bytes[3]!) !== "RSPG") throw new Error(`${label}: not a rive-spine pages bundle`);
  const headerLength = view.getUint32(4, true);
  const header = JSON.parse(new TextDecoder().decode(bytes.subarray(8, 8 + headerLength))) as PagesHeader;
  const start = 8 + headerLength;
  const entries = new Map(header.pages.map((entry) => [entry.file, entry]));
  const undecoded: RiveAtlasPages["undecoded"] = [];
  const pages = await Promise.all(
    names.map(async (name) => {
      const entry = entries.get(name);
      if (!entry) throw new Error(`${label}: no page ${name}`);
      const url = URL.createObjectURL(new Blob([bytes.subarray(start + entry.offset, start + entry.offset + entry.length)], { type: entry.type }));
      const image = new Image();
      image.src = url;
      try {
        await image.decode();
      } catch (error) {
        const reason = error instanceof Error ? error.message : String(error);
        if (!(await loaded(image))) throw new Error(`${label}: page ${name} did not decode: ${reason}`);
        undecoded.push({ name, reason });
      } finally {
        URL.revokeObjectURL(url);
      }
      return [name, new ImageSource({ resource: image, alphaMode: "premultiply-alpha-on-upload", ...RIVE_ATLAS_TEXTURE_OPTIONS })] as const;
    }),
  );
  const sources: Record<string, TextureSource> = Object.fromEntries(pages);
  markRiveSolidPages(sources);
  return { sources, undecoded };
}

/** Whether `image` loads (its data in, not yet decoded), once it has or has failed. */
function loaded(image: HTMLImageElement): Promise<boolean> {
  if (image.complete) return Promise.resolve(image.naturalWidth > 0);
  return new Promise((resolve) => {
    image.addEventListener("load", () => resolve(image.naturalWidth > 0), { once: true });
    image.addEventListener("error", () => resolve(false), { once: true });
  });
}

/** The batcher name Spine slots are drawn with once installed. */
const RIVE_SPINE_BATCHER = "riveSpine";

/** A vertex's level of detail when the GPU's derivatives decide (a mesh). */
const GPU_LOD = -1000;

/** A vertex's level of detail marking a solid colour fill (a page `decodeRiveAtlasPages` read as one). */
const SOLID_LOD = -2000;

/** The converter's textures of a solid colour (a Rive shape filled with one, drawn as an image): their pages' names. */
const SOLID_PAGE = /^rive-solid-[0-9a-f]{6}\./;

/** Pages holding a solid colour: drawn as Rive draws a solid fill (see `riveDitherBitGl`). */
const solidSources = new WeakSet<TextureSource>();

/**
 * Marks a converted skeleton's pages of one solid colour (`rive-solid-RRGGBB.*`: a Rive shape filled with one colour,
 * which the converter draws as an image of it), so they draw as Rive draws a solid fill: premultiplied by the draw's
 * 8-bit alpha in integers.
 */
function markRiveSolidPages(pages: Record<string, TextureSource>): void {
  for (const [name, source] of Object.entries(pages)) if (SOLID_PAGE.test(name)) solidSources.add(source);
}

/** Vertex: x, y, u, v, colour (4 floats), dark colour, texture id + round, level of detail. */
const VERTEX_SIZE = 11;

class RiveSpineGeometry extends Geometry {
  constructor() {
    const attributeBuffer = new Buffer({ data: new Float32Array(1), label: "rive-spine-attributes", usage: BufferUsage.VERTEX | BufferUsage.COPY_DST, shrinkToFit: false });
    const indexBuffer = new Buffer({ data: new Uint32Array(1), label: "rive-spine-indices", usage: BufferUsage.INDEX | BufferUsage.COPY_DST, shrinkToFit: false });
    const stride = VERTEX_SIZE * 4;
    super({
      attributes: {
        aPosition: { buffer: attributeBuffer, format: "float32x2", stride, offset: 0 },
        aUV: { buffer: attributeBuffer, format: "float32x2", stride, offset: 8 },
        aColor: { buffer: attributeBuffer, format: "float32x4", stride, offset: 16 },
        aDarkColor: { buffer: attributeBuffer, format: "unorm8x4", stride, offset: 32 },
        aTextureIdAndRound: { buffer: attributeBuffer, format: "uint16x2", stride, offset: 36 },
        aLod: { buffer: attributeBuffer, format: "float32", stride, offset: 40 },
      },
      indexBuffer,
    });
  }
}

/** The texture read at an explicit level of detail, per texture of the batch (constant indices, as GLSL ES 3 wants). */
function riveTextureBitGl(maxTextures: number) {
  const branches: string[] = [];
  for (let i = 0; i < maxTextures; i++) {
    if (i > 0) branches.push("else");
    if (i < maxTextures - 1) branches.push(`if (vTextureId < ${i}.5)`);
    branches.push(`{
      outColor = vLod > ${GPU_LOD + 1}.0 ? textureLod(uTextures[${i}], vUV, vLod) : texture(uTextures[${i}], vUV, ${RIVE_MIP_LOD_BIAS.toFixed(1)});
    }`);
  }
  return {
    name: "rive-texture-batch-bit",
    vertex: {
      header: `
        in vec2 aTextureIdAndRound;
        in float aLod;
        out float vTextureId;
        out float vLod;
      `,
      main: `
        vTextureId = aTextureIdAndRound.y;
        vLod = aLod;
      `,
    },
    fragment: {
      header: `
        in float vTextureId;
        in float vLod;
        uniform sampler2D uTextures[${maxTextures}];
      `,
      main: `
        ${branches.join("\n")}
      `,
    },
  };
}

/**
 * Rive's dither (DitherMode::interleavedGradientNoise, its default): every drawn fragment's premultiplied colour gets
 * ±½/256 of interleaved gradient noise before blending (shader `applyDither`, gpu.cpp ditherScale 1/256, bias -½/256),
 * so dozens of faint layers round without drifting. Same expression, same precisions.
 */
function riveDitherBitGl() {
  // The noise's argument is written as Rive writes it: the GPU compiler fuses multiply-adds by the expression's shape,
  // and a pixel whose sum lands on an integer reads noise near 0 or near 1 depending on the last bit.
  return {
    name: "rive-dither-bit",
    fragment: {
      end: `
        if (finalColor.a != 0.0) {
          vec2 riveFrag = gl_FragCoord.xy;
          mediump float riveNoise = fract(0.06711056 * riveFrag.x + 0.00583715 * riveFrag.y);
          mediump float riveDither = fract(52.9829189 * riveNoise) * 0.00390625 + -0.001953125;
          finalColor.rgb += riveDither;
        }
        // A solid colour fill: Rive premultiplies its colour by the draw's 8-bit alpha on the CPU, in integers
        // (gpu.cpp SwizzleRiveColorToRGBAPremul: (c·a + 127) / 255), so it reaches blending as exact 8-bit values,
        // which its dither leaves where they are. After the dither, so every other draw compiles as before.
        if (vLod < ${SOLID_LOD + 1}.0) {
          vec3 riveSolid = floor(outColor.rgb * 255.0 + 0.5);
          float riveAlpha = floor(vColor.a * 255.0 + 0.5);
          finalColor = vec4(floor((riveSolid * riveAlpha + 127.0) / 255.0 + 0.0001) / 255.0, riveAlpha / 255.0);
        }
      `,
    },
  };
}

class RiveSpineShader extends Shader {
  constructor(maxTextures: number) {
    // GLSL ES 3.00 (Pixi's batch shaders compile as 1.00, which has no textureSize/textureLod/dFdx): the high-shader
    // source with the version line, which Pixi then keeps (and leaves `in`/`out` alone).
    const { vertex, fragment } = compileHighShaderGl({
      template: { vertex: vertexGlTemplate, fragment: fragmentGlTemplate },
      bits: [globalUniformsBitGl, colorBitGl, darkTintBitGl, riveTextureBitGl(maxTextures), riveDitherBitGl(), roundPixelsBitGl],
    });
    super({
      glProgram: new GlProgram({ name: "rive-spine-batch", vertex: `#version 300 es\n${vertex}`, fragment: `#version 300 es\n${fragment}`, preferredFragmentPrecision: "highp" }),
      resources: { batchSamplers: getBatchSamplersUniformGroup(maxTextures) },
    });
  }
}

let sharedShader: RiveSpineShader | null = null;
/** Device pixels per Pixi unit where Spine is drawn now (`setRiveSpineResolution`). */
let resolution = 1;

/** What packing reads of a spine-pixi-v8 BatchableSpineSlot. */
interface SpineSlotElement {
  renderable: { skeleton: Skeleton; groupAlpha: number; groupColor: number };
  data: { id: string; riveAspect?: number; color: { r: number; g: number; b: number; a: number } };
  darkColor: number;
  roundPixels: number;
  transform: { a: number; b: number; c: number; d: number; tx: number; ty: number };
  positions: Float32Array;
  uvs: Float32Array;
  attributeOffset: number;
  attributeSize: number;
  texture: { source: TextureSource };
}

/** The image's width over height for a region attachment (Rive's image draw), 0 for anything else (a mesh). */
function aspectOf(element: SpineSlotElement): number {
  const data = element.data;
  if (data.riveAspect !== undefined) return data.riveAspect;
  const slot = element.renderable.skeleton.slots[Number.parseInt(data.id, 10)];
  const attachment = slot?.getAttachment();
  const aspect = attachment instanceof RegionAttachment && attachment.region ? attachment.region.originalWidth / attachment.region.originalHeight : 0;
  data.riveAspect = aspect;
  return aspect;
}

/**
 * Rive's level of detail for an image draw (gpu.cpp), from three of its vertices: their device-pixel positions and
 * their texels give the texel → pixel map; its inverse gives the derivatives Rive's formula takes. A quad, or the
 * polygon a clipping attachment cut out of it (the map is the same affine one): the widest triangle of its vertices.
 */
function riveImageLod(element: SpineSlotElement, aspect: number): number {
  const { a, b, c, d, tx, ty } = element.transform;
  const { positions, uvs } = element;
  const scale = resolution;
  const { pixelWidth, pixelHeight } = element.texture.source;
  const o = element.attributeOffset;
  const px = (i: number): [number, number] => {
    const x = positions[i * 2]!;
    const y = positions[i * 2 + 1]!;
    return [(a * x + c * y + tx) * scale, (b * x + d * y + ty) * scale];
  };
  const tex = (i: number): [number, number] => [uvs[i * 2]! * pixelWidth, uvs[i * 2 + 1]! * pixelHeight];
  let [i1, i2] = [o + 1, o + 2];
  if (element.attributeSize > 3) {
    let best = -1;
    const first = tex(o);
    for (let i = o + 1; i < o + element.attributeSize; i++)
      for (let j = i + 1; j < o + element.attributeSize; j++) {
        const ti = tex(i);
        const tj = tex(j);
        const area = Math.abs((ti[0] - first[0]) * (tj[1] - first[1]) - (tj[0] - first[0]) * (ti[1] - first[1]));
        if (area > best) [best, i1, i2] = [area, i, j];
      }
  }
  const [p0, p1, p2] = [px(o), px(i1), px(i2)];
  const [t0, t1, t2] = [tex(o), tex(i1), tex(i2)];
  // Texel edges E and pixel edges F (as columns): the texel → pixel map is J = F·E⁻¹.
  const e = [t1[0] - t0[0], t2[0] - t0[0], t1[1] - t0[1], t2[1] - t0[1]];
  const f = [p1[0] - p0[0], p2[0] - p0[0], p1[1] - p0[1], p2[1] - p0[1]];
  const detE = e[0]! * e[3]! - e[1]! * e[2]!;
  if (detE === 0) return RIVE_MIP_LOD_BIAS;
  const ei = [e[3]! / detE, -e[1]! / detE, -e[2]! / detE, e[0]! / detE];
  const j = [f[0]! * ei[0]! + f[1]! * ei[2]!, f[0]! * ei[1]! + f[1]! * ei[3]!, f[2]! * ei[0]! + f[3]! * ei[2]!, f[2]! * ei[1]! + f[3]! * ei[3]!];
  const detJ = j[0]! * j[3]! - j[1]! * j[2]!;
  if (detJ === 0) return RIVE_MIP_LOD_BIAS;
  // J⁻¹: pixel → texel.
  const dUdX = j[3]! / detJ;
  const dUdY = -j[1]! / detJ;
  const dVdX = -j[2]! / detJ;
  const dVdY = j[0]! / detJ;
  const rho2 = Math.max(dUdX * dUdX + aspect * aspect * dVdX * dVdX, (dUdY * dUdY) / (aspect * aspect) + dVdY * dVdY);
  return 0.5 * Math.log2(Math.max(rho2, 1)) + RIVE_MIP_LOD_BIAS;
}

/** Draws Spine slots (spine-pixi-v8's dark-tint packing, with float colours and Rive's level of detail). */
export class RiveSpineBatcher extends Batcher {
  static extension = { type: [ExtensionType.Batcher] as const, name: RIVE_SPINE_BATCHER };
  geometry = new RiveSpineGeometry();
  shader: Shader = (sharedShader ??= new RiveSpineShader(this.maxTextures));
  name = RIVE_SPINE_BATCHER;
  vertexSize = VERTEX_SIZE;

  packAttributes(meshElement: BatchableMeshElement, float32View: Float32Array, uint32View: Uint32Array, index: number, textureId: number): void {
    const element = meshElement as unknown as SpineSlotElement;
    const textureIdAndRound = (textureId << 16) | (element.roundPixels & 0xffff);
    const { a, b, c, d, tx, ty } = element.transform;
    const { positions, uvs } = element;
    // The slot's colour (skeleton × slot × attachment) times the Spine object's group tint and alpha. Its opacity is
    // rounded to 8 bits as Rive rounds each image draw's (colorModulateOpacity: lround(255·opacity), in float32), where
    // Pixi's own packing truncates: a layer at 0.7/255 draws at 1/255 in Rive, at 0 in Pixi.
    const color = element.data.color;
    const group = element.renderable.groupColor;
    const r = color.r * ((group & 0xff) / 255);
    const g = color.g * (((group >> 8) & 0xff) / 255);
    const bl = color.b * (((group >> 16) & 0xff) / 255);
    const alpha = Math.round(Math.fround(255 * Math.fround(Math.min(1, Math.max(0, color.a * element.renderable.groupAlpha))))) / 255;
    const darkColor = Color.shared.setValue(element.darkColor).premultiply(alpha, true).toPremultiplied(1, false);
    const aspect = aspectOf(element);
    const lod = solidSources.has(element.texture.source) ? SOLID_LOD : aspect > 0 && element.attributeSize >= 3 ? riveImageLod(element, aspect) : GPU_LOD;
    const end = element.attributeOffset + element.attributeSize;
    for (let i = element.attributeOffset; i < end; i++) {
      const x = positions[i * 2]!;
      const y = positions[i * 2 + 1]!;
      float32View[index++] = a * x + c * y + tx;
      float32View[index++] = d * y + b * x + ty;
      float32View[index++] = uvs[i * 2]!;
      float32View[index++] = uvs[i * 2 + 1]!;
      float32View[index++] = r;
      float32View[index++] = g;
      float32View[index++] = bl;
      float32View[index++] = alpha;
      uint32View[index++] = darkColor;
      uint32View[index++] = textureIdAndRound;
      float32View[index++] = lod;
    }
  }

  packQuadAttributes(_element: BatchableQuadElement): void {
    throw new Error("rive-spine: Spine slots are never packed as quads");
  }
}

let installed = false;

/** Draws Spine slots with Rive's texture sampling from now on (before the first Spine render; once is enough). */
export function installRiveTextureSampling(): void {
  if (installed) return;
  installed = true;
  extensions.add(RiveSpineBatcher);
  const proto = BatchableSpineSlot.prototype as unknown as { setData: (...args: unknown[]) => void; batcherName: string };
  const setData = proto.setData;
  proto.setData = function (this: { batcherName: string }, ...args: unknown[]): void {
    setData.apply(this, args);
    this.batcherName = RIVE_SPINE_BATCHER;
  };
}

/** The device pixels per Pixi unit where Spine is drawn next (an offscreen texture's resolution), for Rive's mip level. */
export function setRiveSpineResolution(devicePixelsPerUnit: number): void {
  resolution = devicePixelsPerUnit;
}
